import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "react-query";
import { QUERY_KEYS } from "./constants";
import {
  getUserAddresses,
  saveUserAddress,
  updateUserAddress,
  deleteUserAddress,
  getKeyStats,
} from "../user";
import {
  listPaymentMethods,
  createSetupIntent,
  getPaymentMethod,
  updatePaymentMethod,
  detachPaymentMethod,
} from "../payment";
import { cancelOrder, getOrderById, getOrders } from "../orders";
import { getQuote, bookShipment } from "../ship";
import { toast } from "react-toastify";
import { createTicket } from "../ticket";
import { getNotifications, markAsRead } from "../notifications";
import { updateUserRating } from "../ratings";
import { getHSCodeStatic, searchHSCode } from "../hsCodes";
import {
  confirmPasswordReset,
  requestPasswordReset,
  validateResetToken,
} from "../auth";
import { fetchCountries, fetchSubdivisions } from "../geo";

// --- Dashboard Key Stats ---
export const useGetKeyStats = () => {
  return useQuery(QUERY_KEYS.GET_KEY_STATS, getKeyStats, {
    cacheTime: 5000,
    staleTime: 30000, // refresh every 30 seconds if needed
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useGetUserAddresses = (onSuccess) => {
  return useQuery(QUERY_KEYS.GET_ADDRESSES, getUserAddresses, {
    cacheTime: 50000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    onSuccess,
    // select: (data) => onDataLoad(data),
  });
};

export const useSaveUserAddress = (onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation(saveUserAddress, {
    onSuccess: () => {
      toast.success("Address has successfully been saved");
      queryClient.invalidateQueries([QUERY_KEYS.GET_ADDRESSES]);
    },
  });
};

export const useUpdateUserAddress = (onSuccess, onFailure) => {
  const queryClient = useQueryClient();
  return useMutation(updateUserAddress, {
    onSuccess: () => {
      toast.success("Address has successfully been updated");
      queryClient.invalidateQueries([QUERY_KEYS.GET_ADDRESSES]);
    },
  });
};

export const useDeleteUserAddress = () => {
  const queryClient = useQueryClient();

  return useMutation(deleteUserAddress, {
    onSuccess: () => {
      toast.success("Address has successfully been deleted");
      queryClient.invalidateQueries([QUERY_KEYS.GET_ADDRESSES]);
    },
    onError: () => {
      toast.error("Failed to delete address. Please try again.");
    },
  });
};

export const useListPaymentMethods = () => {
  return useQuery(QUERY_KEYS.LIST_PAYMENT_METHODS, listPaymentMethods, {
    cacheTime: 5000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useGetPaymentMethod = (paymentMethodId) => {
  return useQuery(
    [QUERY_KEYS.GET_PAYMENT_METHOD, paymentMethodId],
    async () => {
      if (!paymentMethodId) return null; // no ID → return null immediately
      try {
        const data = getPaymentMethod(paymentMethodId)
        return data || null;
      } catch (err) {
        // If Stripe returned 404 (deleted card), return null
        if (err.response?.status === 404) return null;
        throw err; // rethrow other errors
      }
    },
    {
      enabled: !!paymentMethodId, // don’t fetch if ID is undefined
      retry: false, // avoid retrying repeatedly for missing/deleted cards
    }
  );
};

export const useCreateSetupIntent = () => {
  return useMutation(createSetupIntent);
};

export const useUpdatePaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ paymentMethodId, billingDetails }) =>
      updatePaymentMethod(paymentMethodId, billingDetails),
    {
      onSuccess: () => {
        toast.success("Payment method has been updated");
        queryClient.invalidateQueries([QUERY_KEYS.LIST_PAYMENT_METHODS]);
      },
      onError: (err) => {
        toast.error("Failed to update payment method");
        console.error(err);
      },
    }
  );
};

export const useDetachPaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (paymentMethodId) => detachPaymentMethod(paymentMethodId),
    {
      onSuccess: () => {
        toast.success("Payment method has been removed");
        queryClient.invalidateQueries([QUERY_KEYS.LIST_PAYMENT_METHODS]);
      },
      onError: (err) => {
        toast.error("Failed to remove payment method");
        console.error(err);
      },
    }
  );
};

export const useGetOrderById = (orderId) => {
  return useQuery(
    [QUERY_KEYS.GET_ORDER_BY_ID, orderId],
    () => getOrderById(orderId),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );
};

export const useGetOrders = (orderId) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_ORDERS],
    queryFn: () => getOrders(orderId),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage[0].LastEvaluatedKey) {
        return lastPage[0].LastEvaluatedKey;
      } else {
        return undefined;
      }
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useGetQuote = () => {
  return useMutation(getQuote);
};

export const useConfirmShipment = () => {
  return useMutation(bookShipment);
};

export const useCreateTicket = () => {
  return useMutation(createTicket, {
    onSuccess: () => {
      toast.success("Ticket has successfully been submitted");
    },
  });
};

export const useCancelOrder = () => {
  const queryClient = useQueryClient();

  return useMutation(cancelOrder, {
    onSuccess: () => {
      toast.success("Order has successfully been cancelled");
      queryClient.invalidateQueries([QUERY_KEYS.GET_ORDER_BY_ID]);
    },
  });
};

export const useGetNotifications = () => {
  return useQuery(QUERY_KEYS.GET_NOTIFICATIONS, getNotifications, {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId) => markAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_NOTIFICATIONS]);
    },
  });
};

export const useUpdateUserRating = (onSuccess) => {
  return useMutation({
    mutationFn: updateUserRating,
    onSuccess: () => {
      onSuccess();
    },
  });
};

export const useGetHSCodeStatic = () => {
  return useQuery(QUERY_KEYS.GET_HS_CODES_STATIC, getHSCodeStatic, {
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

export const useSearchHSCode = (query, country, enabled = true) => {
  return useQuery(
    [QUERY_KEYS.GET_HS_CODES_SEARCH, query, country],
    () => searchHSCode(query, country),
    {
      enabled: enabled && !!query && query.length > 0,
      staleTime: 0,
      refetchOnWindowFocus: false,
    }
  );
};

export const useRequestPasswordReset = () => {
  return useMutation({
    mutationFn: requestPasswordReset,
  });
};

export const useConfirmPasswordReset = () => {
  return useMutation({
    mutationFn: confirmPasswordReset,
  });
};

export const useValidateResetToken = (token, uid) => {
  return useQuery(
    [QUERY_KEYS.GET_PASSWORD_RESET_TOKEN_VALIDATION, token, uid],
    () => validateResetToken(token, uid),
    {
      enabled: !!token && !!uid,
    }
  );
};


/**
 * Fetch all supported countries (private)
 */
export const useGetCountries = () => {
  return useQuery(QUERY_KEYS.GET_COUNTRIES, fetchCountries, {
    staleTime: 1000 * 60 * 10, // 10 minutes
    cacheTime: 1000 * 60 * 30, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

/**
 * Fetch subdivisions (provinces/territories) for a country (private)
 * @param {string} countryIso2 - e.g. "CA"
 */
export const useGetSubdivisions = (countryIso2, enabled = true) => {
  return useQuery(
    [QUERY_KEYS.GET_SUB, countryIso2],
    () => fetchSubdivisions(countryIso2),
    {
      enabled: enabled && !!countryIso2,
      staleTime: 1000 * 60 * 10, // 10 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );
};
